import i18n from '@/i18n';
import store from '@/core/store';
import {
   clearBoardExportReturnPath,
   isValidBoardExportReturnPath,
   setBoardExportReturnPath,
   withDefaultBoardDateQuery,
} from '@/core/helpers/boardUrl.helper';
import { NavigationGuard, RouteRecordRaw } from 'vue-router';

const boardExportBeforeEnter: NavigationGuard = (to, from) => {
   // Switching between export tabs — keep the original return URL
   if (from.path.includes('/export')) return;

   if (from.matched.length && isValidBoardExportReturnPath(from.fullPath)) {
      setBoardExportReturnPath(to.params.board_id, from.fullPath);
      return;
   }

   // Direct navigation (no usable previous route)
   clearBoardExportReturnPath(to.params.board_id);
};

const boardRoutes: Array<RouteRecordRaw> = [
   {
      path: '',
      name: 'board-overview',
      meta: {
         title: `${i18n.global.t('global.boards', 2)} - NxtPlan`,
      },
      component: () => import('@/modules/board/pages/BoardOverview.vue'),

      children: [
         {
            path: ':board_id/wizard',
            name: 'board-overview-wizard',
            redirect: { name: 'board-overview-wizard-general' },
            components: {
               default: () => import('@/modules/board/components/overview/BoardWizardModal.vue'),
            },
            children: [
               {
                  path: 'general',
                  name: 'board-overview-wizard-general',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabGeneral.vue'),
                  },
               },
               {
                  path: 'companies',
                  name: 'board-overview-wizard-companies',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabCompanies.vue'),
                  },
               },
               {
                  path: 'team-permissions',
                  name: 'board-overview-wizard-team-permissions',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabTeamPermissions.vue'),
                  },
               },
               {
                  path: 'user-permissions',
                  name: 'board-overview-wizard-user-permissions',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabUserPermissions.vue'),
                  },
               },
               {
                  path: 'parameters',
                  name: 'board-overview-wizard-parameters',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabParameters.vue'),
                  },
               },
               {
                  path: 'timeschedule',
                  name: 'board-overview-wizard-timeschedule',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabTimeschedule.vue'),
                  },
               },
               {
                  path: 'squads',
                  name: 'board-overview-wizard-squads',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabSquads.vue'),
                  },
               },
               {
                  path: 'tabs',
                  name: 'board-overview-wizard-tabs',
                  components: {
                     default: () => import('@/modules/board/components/overview/boardWizardModal/WizardTabTabs.vue'),
                  },
               },
            ],
         },
      ],
   },

   {
      path: ':board_id',
      name: 'board-details',
      components: {
         default: () => import('@/modules/board/components/boardDetails/BoardDetails.vue'),
      },
      meta: {
         parent: 'board-details',
      },
      redirect: (from) => {
         let PATH = getBoardUrlFromLocalStorage(+from.params.board_id?.toString());
         if (PATH?.includes('settings') || PATH?.includes('import') || PATH?.includes('export')) PATH = null;
         const userId = store.state.user?.user_info?.id;
         const boardId = from.params.board_id?.toString();
         const planningView = (userId ? getUserPlanningViewFromLocalStorage(userId, 'board', boardId) : null) ?? 'candidate';

         if (PATH) {
            try {
               const url = new URL(PATH, window.location.origin);
               if (url.pathname.includes('/planning')) {
                  url.searchParams.set('planning_view', planningView);
                  return url.pathname + url.search;
               }
            } catch {
               // fall through to PATH as-is
            }
            return PATH;
         }

         return {
            name: 'board-details-planning',
            query: withDefaultBoardDateQuery({ planning_view: planningView }),
         };
      },
      children: [
         {
            path: 'proposal',
            redirect: (from) => {
               return { name: 'board-details-planning-candidate', params: from.params, query: from.query };
            },
         },
         {
            path: 'planning',
            name: 'board-details-planning',
            components: {
               default: () => import('@/modules/board/components/boardDetails/planning/Planning.vue'),
            },
            meta: {
               title: `${i18n.global.t('board.proposal')} {board.board.name} - NxtPlan`,
            },
         },
         {
            path: 'overview',
            name: 'board-details-overview',
            components: {
               default: () => import('@/modules/board/components/boardDetails/Overview.vue'),
            },
         },
         {
            path: 'time-registration',
            name: 'board-details-timeregistrations',
            components: {
               default: () => import('@/modules/board/components/boardDetails/Timeregistrations.vue'),
            },
            meta: {
               title: `${i18n.global.t('global.timeregistration', 2)} {board.board.name} - NxtPlan`,
            },
         },
         {
            path: 'settings',
            name: 'board-details-settings',
            component: () => import('@/modules/board/components/boardDetails/BoardSettings.vue'),
            redirect: { name: 'board-details-settings-general' },
            meta: {
               title: `${i18n.global.t('board.board_settings')}`,
            },
            children: [
               {
                  path: 'general',
                  name: 'board-details-settings-general',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/General.vue'),
                  },
               },
               {
                  path: 'property-types',
                  name: 'board-details-settings-property-types',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/PropertyTypes.vue'),
                  },
               },
               {
                  path: 'squads',
                  name: 'board-details-settings-squads',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Squads.vue'),
                  },
               },
               {
                  path: 'timetables',
                  name: 'board-details-settings-timetables',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Timetables.vue'),
                  },
               },
               {
                  path: 'time-clocks',
                  name: 'board-details-settings-time-clocks',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/TimeClocks.vue'),
                  },
               },
               {
                  path: 'rights',
                  name: 'board-details-settings-rights',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Permissions.vue'),
                  },
               },
               {
                  path: 'tabs',
                  name: 'board-details-settings-tabs',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/SquadFiltersSettings.vue'),
                  },
               },
               {
                  path: 'tags',
                  name: 'board-details-settings-tags',
                  components: {
                     side_modal: () => import('@/core/components/tags/Tags.vue'),
                  },
               },
               {
                  path: 'contracts',
                  name: 'board-details-settings-contracts',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Contracts.vue'),
                  },
               },
               {
                  path: 'integrations',
                  name: 'board-details-settings-integrations',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Integrations.vue'),
                  },
               },
               {
                  path: 'preferences',
                  name: 'board-details-settings-preferences',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardSettings/Preferences.vue'),
                  },
               },
            ],
         },
         {
            path: 'import',
            name: 'board-details-import',
            component: () => import('@/modules/board/components/boardDetails/BoardImport.vue'),
            redirect: { name: 'board-details-import-needs' },
            meta: {
               title: `${i18n.global.t('board.import')}`,
            },
            children: [
               {
                  path: 'needs',
                  name: 'board-details-import-needs',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardImport/ImportStructure.vue'),
                  },
               },
               {
                  path: 'planning',
                  name: 'board-details-import-planning',
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardImport/ImportPlanning.vue'),
                  },
               },
            ],
         },
         {
            path: 'export',
            name: 'board-details-export',
            component: () => import('@/modules/board/components/boardDetails/BoardExport.vue'),
            redirect: { name: 'board-details-export-old-planning' },
            meta: {
               title: `${i18n.global.t('board.export')}`,
            },
            children: [
               {
                  path: 'old-planning',
                  name: 'board-details-export-old-planning',
                  beforeEnter: boardExportBeforeEnter,
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardExport/ExportBoardPlanning.vue'),
                  },
               },
               {
                  path: 'extended-contract',
                  name: 'board-details-export-extended-contract',
                  beforeEnter: boardExportBeforeEnter,
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardExport/ExportExtendedContract.vue'),
                  },
               },
               {
                  path: 'needs-template',
                  name: 'board-details-export-needs-template',
                  beforeEnter: boardExportBeforeEnter,
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardExport/ExportNeedsTemplate.vue'),
                  },
               },
               {
                  path: 'slots',
                  name: 'board-details-export-slots',
                  beforeEnter: boardExportBeforeEnter,
                  components: {
                     side_modal: () => import('@/modules/board/components/boardDetails/boardExport/ExportSlots.vue'),
                  },
               },
            ],
         },
      ],
   },
];
export default boardRoutes;
